import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchLatestReport, fetchHistoricalReport, fetchInventory, fetchMetrics, getDownloadUrl, getMonthlyReviewUrl, getAuthInfo, clearToken } from '../api';
import KPICards from '../components/KPICards';
import DataTable from '../components/DataTable';
import TrainSchedule from '../components/TrainSchedule';

function formatDateShort(val) {
  if (!val) return '';
  try {
    let dateStr = val;
    if (dateStr.includes('T')) dateStr = dateStr.split('T')[0];
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const yy = parts[0].slice(-2);
      return `${parts[1]}/${parts[2]}/${yy}`;
    }
  } catch (e) {}
  return val;
}

function InventoryPage({ data, accentColor, liveInventory }) {
  // Use live inventory data if available, otherwise fall back to report data
  const inv = liveInventory?.emptyInventory || data.emptyInventory || {};
  const gensets = liveInventory?.gensets || data.gensets;
  const updatedAt = liveInventory?.updatedAt || data.generatedAt;

  const terminals = ['FET', 'AMA'];

  return (
    <div className="space-y-6">
      {updatedAt && (
        <p className="text-xs text-gray-400">
          Last updated: {new Date(updatedAt).toLocaleString('en-US', {
            dateStyle: 'long', timeStyle: 'short', timeZone: 'America/Chicago',
          })} CT{liveInventory?.updatedAt ? ' (updates hourly)' : ''}
        </p>
      )}

      <h3 className="text-sm font-bold" style={{ color: accentColor }}>Empty Containers</h3>
      {terminals.map((terminal) => {
        const items = inv[terminal] || [];
        if (items.length === 0) return (
          <div key={terminal}>
            <h4 className="text-xs font-semibold mb-1 text-gray-600">
              {terminal === 'AMA' ? 'Amarillo (AMA)' : 'Fremont (FET)'}
            </h4>
            <p className="text-gray-500 text-xs">No empty inventory data.</p>
          </div>
        );

        const byCarrier = {};
        items.forEach((item) => {
          if (!byCarrier[item.carrier]) byCarrier[item.carrier] = [];
          byCarrier[item.carrier].push(item);
        });

        return (
          <div key={terminal}>
            <h4 className="text-xs font-semibold mb-1 text-gray-600">
              {terminal === 'AMA' ? 'Amarillo (AMA)' : 'Fremont (FET)'}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Carrier</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(byCarrier).sort().map(([carrier, sizes]) =>
                    sizes.sort((a, b) => a.size.localeCompare(b.size)).map((item, idx) => (
                      <tr key={`${carrier}-${item.size}`} className="hover:bg-gray-50">
                        <td className="px-2 py-1 font-medium">{idx === 0 ? carrier : ''}</td>
                        <td className="px-2 py-1">{item.size}</td>
                        <td className="px-2 py-1 text-right">{item.count}</td>
                      </tr>
                    ))
                  )}
                  <tr className="bg-gray-50 font-medium">
                    <td className="px-2 py-1" colSpan={2}>Total</td>
                    <td className="px-2 py-1 text-right">{items.reduce((sum, i) => sum + i.count, 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Gensets section at the bottom */}
      {gensets && (
        <>
          <hr className="border-gray-200 my-4" />
          <h3 className="text-sm font-bold" style={{ color: accentColor }}>Gensets</h3>
          {terminals.map((terminal) => {
            const items = gensets[terminal] || [];
            if (items.length === 0) return (
              <div key={`genset-${terminal}`}>
                <h4 className="text-xs font-semibold mb-1 text-gray-600">
                  {terminal === 'AMA' ? 'Amarillo (AMA)' : 'Fremont (FET)'}
                </h4>
                <p className="text-gray-500 text-xs">No gensets at this terminal.</p>
              </div>
            );

            const carrierCounts = {};
            items.forEach((g) => {
              const c = g.carrier || 'Unknown';
              carrierCounts[c] = (carrierCounts[c] || 0) + 1;
            });

            return (
              <div key={`genset-${terminal}`}>
                <h4 className="text-xs font-semibold mb-1 text-gray-600">
                  {terminal === 'AMA' ? 'Amarillo (AMA)' : 'Fremont (FET)'}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Carrier</th>
                        <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(carrierCounts).sort().map(([carrier, count]) => (
                        <tr key={carrier} className="hover:bg-gray-50">
                          <td className="px-2 py-1">{carrier}</td>
                          <td className="px-2 py-1 text-right">{count}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-medium">
                        <td className="px-2 py-1">Total</td>
                        <td className="px-2 py-1 text-right">{items.length}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function formatMonth(monthStr) {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
}

function TransitMetrics({ data, accentColor, customer }) {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const lastMonth = now.getMonth() === 0
    ? `${now.getFullYear() - 1}-12`
    : `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`;

  const [startMonth, setStartMonth] = useState(lastMonth);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [customMetrics, setCustomMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // Use report-embedded metrics by default, custom API metrics when user changes range
  const rawMetrics = customMetrics || data.transitMetrics;

  let metricsMap;
  if (rawMetrics && !Array.isArray(rawMetrics) && typeof rawMetrics === 'object') {
    // If from API it's wrapped in { transitMetrics: {...} }
    if (rawMetrics.transitMetrics) {
      metricsMap = rawMetrics.transitMetrics;
    } else {
      metricsMap = rawMetrics;
    }
  } else if (Array.isArray(rawMetrics)) {
    metricsMap = { all: rawMetrics };
  } else {
    metricsMap = { all: [] };
  }

  const allMetrics = metricsMap.all || [];
  const fetMetrics = metricsMap.FET || [];
  const amaMetrics = metricsMap.AMA || [];

  const handleApplyRange = async () => {
    setLoadingMetrics(true);
    try {
      const result = await fetchMetrics(customer, startMonth, endMonth);
      setCustomMetrics(result);
    } catch (e) {
      console.error('Error fetching metrics:', e);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleReset = () => {
    setStartMonth(lastMonth);
    setEndMonth(currentMonth);
    setCustomMetrics(null);
  };

  const months = allMetrics.map((r) => r.month);

  const metricDefs = [
    { key: 'exitToRail', label: 'Exit Plant to Released on Rail' },
    { key: 'railToPort', label: 'Released on Rail to Deramped at POL' },
    { key: 'railTransit', label: 'Rail Transit Time' },
    { key: 'portToSail', label: 'Deramped at POL to Confirmed Sailing' },
    { key: 'exitToSail', label: 'Exit Plant to Confirmed Sailing' },
  ];

  const statLabels = [
    { key: 'avg', label: 'Avg' },
    { key: 'median', label: 'Med' },
    { key: 'min', label: 'Min' },
    { key: 'max', label: 'Max' },
  ];

  const terminals = [
    { key: 'all', label: 'Combined', data: allMetrics },
    { key: 'FET', label: 'Fremont', data: fetMetrics },
    { key: 'AMA', label: 'Amarillo', data: amaMetrics },
  ];

  const renderStatCells = (stats, prefix) => {
    if (stats) {
      return (
        <>
          {statLabels.map((s, i) => (
            <td key={`${prefix}-${s.key}`} className={`px-2 py-1 text-right whitespace-nowrap font-medium${i === 0 ? ' border-l' : ''}`}>
              {stats[s.key] != null ? stats[s.key] : '—'}
            </td>
          ))}
          <td key={`${prefix}-n`} className="px-2 py-1 text-right whitespace-nowrap text-gray-400">{stats.n || 0}</td>
        </>
      );
    }
    return (
      <>
        {statLabels.map((s, i) => (
          <td key={`${prefix}-${s.key}`} className={`px-2 py-1 text-right text-gray-300${i === 0 ? ' border-l' : ''}`}>—</td>
        ))}
        <td key={`${prefix}-n`} className="px-2 py-1 text-right text-gray-300">0</td>
      </>
    );
  };

  return (
    <div>
      {/* Date range picker */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="text-xs text-gray-600 font-medium">Date Range:</label>
        <input
          type="month"
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          className="px-2 py-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <span className="text-xs text-gray-400">to</span>
        <input
          type="month"
          value={endMonth}
          onChange={(e) => setEndMonth(e.target.value)}
          className="px-2 py-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <button
          onClick={handleApplyRange}
          disabled={loadingMetrics}
          className="px-3 py-1 rounded text-xs font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: accentColor }}
        >
          {loadingMetrics ? 'Loading...' : 'Apply'}
        </button>
        {customMetrics && (
          <button onClick={handleReset} className="text-xs text-gray-500 hover:text-gray-700 underline">
            Reset
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400 mb-1">Transit days by month (based on ingate/exit plant date).</p>
      <p className="text-xs text-gray-400 mb-3">Rail Transit Time = day container deramped at port minus day train departed origin (Fremont/Zita) on BNSF.</p>

      {allMetrics.length === 0 ? (
        <p className="text-gray-500 text-xs py-4">No transit metrics available for this period.</p>
      ) : (
        <div className="space-y-5">
          {metricDefs.map((metric) => (
            <div key={metric.key}>
              <h4 className="text-xs font-semibold mb-1" style={{ color: accentColor }}>{metric.label}</h4>
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase" rowSpan={2}>Month</th>
                      {terminals.map((t) => (
                        <th
                          key={t.key}
                          className="px-2 py-1 text-center text-xs font-semibold uppercase border-l"
                          colSpan={statLabels.length + 1}
                          style={{ color: accentColor }}
                        >
                          {t.label}
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-gray-50 border-b">
                      {terminals.map((t) => (
                        <React.Fragment key={`hdr-${t.key}`}>
                          {statLabels.map((s, si) => (
                            <th key={`${t.key}-${s.key}`} className={`px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase${si === 0 ? ' border-l' : ''}`}>{s.label}</th>
                          ))}
                          <th key={`${t.key}-n`} className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">N</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {months.map((month, idx) => (
                      <tr key={month} className="hover:bg-gray-50">
                        <td className="px-2 py-1 font-medium whitespace-nowrap">{formatMonth(month)}</td>
                        {terminals.map((t) => {
                          const row = t.data[idx];
                          const stats = row ? row[metric.key] : null;
                          return (
                            <React.Fragment key={`${month}-${t.key}`}>
                              {renderStatCells(stats, `${month}-${t.key}`)}
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ historical }) {
  const { customer, date } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('atTerminal');
  const [globalSearch, setGlobalSearch] = useState('');
  const [liveInventory, setLiveInventory] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetcher = historical && date
      ? fetchHistoricalReport(customer, date)
      : fetchLatestReport(customer);

    fetcher
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [customer, date, historical]);

  // Fetch live inventory when inventory tab is selected
  const refreshInventory = useCallback(() => {
    fetchInventory(customer)
      .then(setLiveInventory)
      .catch(() => {}); // silently fall back to report data
  }, [customer]);

  useEffect(() => {
    if (activeTab === 'inventory') {
      refreshInventory();
    }
  }, [activeTab, refreshInventory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500 text-lg">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Per-customer theme overrides (applied client-side until next report regen)
  const THEME_OVERRIDES = {
    tyson: { accentColor: '#FED34C', accentText: 'dark', headerBg: '#FED34C', headerText: '#333', logoFilter: 'none', logo: '/tyson-logo.svg' },
    seaboard: { accentColor: '#003876', accentText: 'light', headerBg: '#003876', headerText: '#ffffff', logoFilter: 'none', logo: '/seaboard-logo.svg' },
    jbs: { accentColor: '#1B1464', accentText: 'light', headerBg: '#1B1464', headerText: '#ffffff', logoFilter: 'brightness(0) invert(1)', logo: '/jbs-logo.svg' },
  };
  const override = THEME_OVERRIDES[customer] || {};
  const accentColor = override.accentColor || data.config?.accentColor || '#1F6B75';
  const accentText = override.accentText || data.config?.accentText || 'light';
  const headerBg = override.headerBg || accentColor;
  const headerText = override.headerText || (accentText === 'dark' ? '#333' : '#fff');
  const customerLogo = override.logo || (data.config?.logo ? `/${data.config.logo}-logo.svg` : null);
  const reportDate = data.generatedAt
    ? new Date(data.generatedAt).toLocaleString('en-US', {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone: 'America/Chicago',
      })
    : 'Unknown';

  const today = data.generatedAt ? data.generatedAt.split('T')[0] : new Date().toISOString().split('T')[0];

  // At Terminal columns
  const atCols = [
    { key: 'booking', label: 'Booking #' },
    { key: 'containerNumber', label: 'Container #' },
    { key: 'order', label: 'Order' },
  ];
  if (data.config?.hasLoadingLocation) {
    atCols.push({ key: 'loadingLocation', label: 'Load Loc' });
  }
  atCols.push(
    { key: 'size', label: 'Size' },
    { key: 'carrier', label: 'Carrier' },
    { key: 'terminal', label: 'Term' },
  );
  if (data.config?.hasPolPod) {
    atCols.push({ key: 'pod', label: 'POD' });
  }
  atCols.push(
    { key: 'ingateDate', label: 'Ingate', format: 'date' },
    { key: 'expectedTrain', label: 'Exp Train' },
    { key: 'dwellDays', label: 'Dwell', format: 'dwell' },
    { key: 'vessel', label: 'Vessel' },
    { key: 'portCut', label: 'Port Cut', format: 'date' },
    { key: 'sailDate', label: 'Sail Date', format: 'date' },
  );

  // On Train columns
  const onCols = [
    { key: 'booking', label: 'Booking #' },
    { key: 'containerNumber', label: 'Container #' },
    { key: 'order', label: 'Order' },
  ];
  if (data.config?.hasLoadingLocation) {
    onCols.push({ key: 'loadingLocation', label: 'Load Loc' });
  }
  onCols.push(
    { key: 'size', label: 'Size' },
    { key: 'carrier', label: 'Carrier' },
    { key: 'terminal', label: 'Term' },
    { key: 'trainNumber', label: 'Train #' },
    { key: 'currentLocation', label: 'Location' },
  );
  if (data.config?.hasPolPod) {
    onCols.push({ key: 'pol', label: 'POL' }, { key: 'pod', label: 'POD' });
  }
  onCols.push(
    { key: 'ingateDate', label: 'Ingate', format: 'date' },
    { key: 'railOutDate', label: 'Rail Out', format: 'date' },
    { key: 'etaPol', label: 'ETA Port', format: 'date' },
    { key: 'dwellDays', label: 'Dwell', format: 'dwell' },
    { key: 'vessel', label: 'Vessel' },
    { key: 'portCut', label: 'Port Cut', format: 'date' },
    { key: 'sailDate', label: 'Sail Date', format: 'date' },
  );

  // At Port columns
  const portCols = [
    { key: 'booking', label: 'Booking #' },
    { key: 'containerNumber', label: 'Container #' },
    { key: 'order', label: 'Order' },
  ];
  if (data.config?.hasLoadingLocation) {
    portCols.push({ key: 'loadingLocation', label: 'Load Loc' });
  }
  portCols.push(
    { key: 'size', label: 'Size' },
    { key: 'carrier', label: 'Carrier' },
    { key: 'terminal', label: 'Term' },
  );
  if (data.config?.hasPolPod) {
    portCols.push({ key: 'pol', label: 'POL' }, { key: 'pod', label: 'POD' });
  }
  portCols.push(
    { key: 'ingateDate', label: 'Ingate', format: 'date' },
    { key: 'railOutDate', label: 'Rail Out', format: 'date' },
    { key: 'arrivedAtPort', label: 'Port Arrival', format: 'date' },
    { key: 'dwellDays', label: 'Dwell', format: 'dwell' },
    { key: 'vessel', label: 'Vessel' },
    { key: 'portCut', label: 'Port Cut', format: 'date' },
    { key: 'sailDate', label: 'Sail Date', format: 'date' },
  );

  const authInfo = getAuthInfo();
  const isAdmin = authInfo?.is_admin;

  const handleLogout = () => {
    clearToken();
    navigate('/');
  };

  // Global search: count matches across all three container tabs
  const globalMatchCount = globalSearch ? (() => {
    const q = globalSearch.toLowerCase();
    const allRows = [...(data.atTerminal || []), ...(data.onTrain || []), ...(data.atPort || [])];
    return allRows.filter((row) =>
      Object.values(row).some((v) => String(v || '').toLowerCase().includes(q))
    ).length;
  })() : null;

  const tabs = [
    { id: 'atTerminal', label: `At Terminal (${data.atTerminal?.length || 0})` },
    { id: 'onTrain', label: `On Train (${data.onTrain?.length || 0})` },
    { id: 'atPort', label: `At Port (${data.atPort?.length || 0})` },
    { id: 'trainSchedule', label: 'Train Schedule' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'metrics', label: 'Metrics' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="px-6 py-3" style={{
        backgroundColor: headerBg,
        color: headerText,
        borderBottom: headerBg === '#ffffff' ? `3px solid ${accentColor}` : 'none',
      }}>
        <div className="max-w-full mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {customerLogo && (
              <img
                src={customerLogo}
                alt={`${data.customer} logo`}
                style={{
                  height: '60px',
                  width: 'auto',
                  maxWidth: '200px',
                  filter: override.logoFilter || (accentText === 'light' ? 'brightness(0) invert(1)' : 'none'),
                }}
              />
            )}
            <div>
              <h1 className="text-xl font-bold">
                {data.customer} — Container Report
              </h1>
              <p className="text-xs mt-0.5" style={{ opacity: 0.7 }}>
                {historical ? `Historical report: ${date}` : `Last updated: ${reportDate} CT`}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href={getDownloadUrl(customer, date || today)}
              className="px-3 py-1.5 rounded text-xs font-medium transition"
              style={{
                backgroundColor: headerBg === '#ffffff' ? accentColor : (accentText === 'dark' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)'),
                color: headerBg === '#ffffff' ? '#fff' : (accentText === 'dark' ? '#333' : '#fff'),
              }}
            >
              Download Excel
            </a>
            <button
              onClick={() => navigate(`/history/${customer}`)}
              className="px-3 py-1.5 rounded text-xs font-medium transition"
              style={{
                backgroundColor: headerBg === '#ffffff' ? accentColor : (accentText === 'dark' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)'),
                color: headerBg === '#ffffff' ? '#fff' : (accentText === 'dark' ? '#333' : '#fff'),
              }}
            >
              History
            </button>
            {isAdmin && (
              <a
                href={getMonthlyReviewUrl(customer)}
                className="px-3 py-1.5 rounded text-xs font-medium transition"
                style={{
                  backgroundColor: headerBg === '#ffffff' ? accentColor : (accentText === 'dark' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)'),
                  color: headerBg === '#ffffff' ? '#fff' : (accentText === 'dark' ? '#333' : '#fff'),
                }}
              >
                Monthly Review
              </a>
            )}
            {isAdmin && (
              <select
                value={customer}
                onChange={(e) => navigate(`/dashboard/${e.target.value}`)}
                className="px-3 py-1.5 rounded text-xs font-medium transition appearance-none cursor-pointer"
                style={{
                  backgroundColor: accentText === 'dark' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)',
                  color: accentText === 'dark' ? '#333' : '#fff',
                }}
              >
                {(authInfo.customers || []).map((c) => (
                  <option key={c} value={c} style={{ color: '#333' }}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 rounded text-xs font-medium transition"
              style={{
                backgroundColor: headerBg === '#ffffff' ? accentColor : (accentText === 'dark' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)'),
                color: headerBg === '#ffffff' ? '#fff' : (accentText === 'dark' ? '#333' : '#fff'),
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-4 py-4">
        {/* KPI Cards */}
        <KPICards summary={data.summary} accentColor={accentColor} accentText={accentText} />

        {/* Global Search */}
        <div className="mt-3 flex items-center gap-3">
          <input
            type="text"
            placeholder="Search across all tabs (At Terminal, On Train, At Port)..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            className="px-3 py-2 border rounded w-96 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          {globalSearch && (
            <>
              <span className="text-sm text-gray-500">{globalMatchCount} matches</span>
              <button
                onClick={() => setGlobalSearch('')}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                ✕ Clear
              </button>
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-3 bg-white rounded-lg shadow">
          <div className="border-b border-gray-200 flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-xs font-medium border-b-2 transition whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-current text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                style={activeTab === tab.id ? { color: accentColor, borderColor: accentColor } : {}}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-3">
            {activeTab === 'atTerminal' && (
              <DataTable columns={atCols} rows={data.atTerminal || []}
                note="Dwell = days since terminal ingate" globalFilter={globalSearch} />
            )}
            {activeTab === 'onTrain' && (
              <DataTable columns={onCols} rows={data.onTrain || []}
                note="Dwell = days since rail departure from terminal" globalFilter={globalSearch} />
            )}
            {activeTab === 'atPort' && (
              <DataTable columns={portCols} rows={data.atPort || []}
                note="Dwell = days since port arrival (BNSF DERAMP)" globalFilter={globalSearch} />
            )}
            {activeTab === 'trainSchedule' && (
              <TrainSchedule
                schedule={data.trainSchedule || {}}
                accentColor={accentColor}
              />
            )}
            {activeTab === 'inventory' && (
              <InventoryPage data={data} accentColor={accentColor} liveInventory={liveInventory} />
            )}
            {activeTab === 'metrics' && (
              <TransitMetrics data={data} accentColor={accentColor} customer={customer} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
